/**
 * 装配体零件拖拽策略。
 * 职责：
 * 1. 实现装配体环境下零件或子装配实例的直接拖拽；
 * 2. 点击 imported-model 子 mesh 时，始终对其归一后的整体实例组进行移动或旋转；
 * 3. 复用移动零部件工具的主模式、轴向约束与碰撞检测能力；
 * 4. 碰撞检测只消费 `raycast-acceleration` 预先构建完成的 `boundsTree`，不在拖拽流程内生成 BVH。
 */

import * as THREE from 'three';
import type { Object3D as WebGpuObject3D } from 'three/webgpu';
import type { Viewer } from '../../viewer/Viewer';
import type { RawPointerEventPayload } from '../../viewer/moudles/EventManager';
import {
    AssemblyTransformToolMode,
    MoveComponentCollisionCheckScope,
    MoveAlongEntityConstraintKind,
    MoveComponentInputMode,
    type MoveComponentCollisionDetectionConfig,
} from '../../assembly/move-component/MoveComponentTypes';
import { MateSolveStatus } from '../../assembly/definition';
import type { AssemblyMateComponentRotationAxis } from '../../assembly/mate';
import type { MoveAlongEntityResolvedReference } from '../../assembly/move-component/move/MoveAlongEntityReferenceResolver';
import { AssemblyConstrainedRotationProjector } from '../../assembly/move-component/rotate/AssemblyConstrainedRotationProjector';
import { recomputeAssemblyDatumReferences } from '../../reference/shared/recomputeAssemblyDatumReferences';
import { AssemblyComponentFixationService } from '../../assembly/component-fixation';
import {
    applyComponentInstanceSnapshotToObject,
    areComponentInstanceSnapshotsEqual,
    createAssemblyTransformHistoryRecord,
    createComponentInstanceSnapshotFromObject,
    type AssemblyTransformHistoryRecord,
    type ComponentInstanceSnapshot,
} from '../../assembly/component-instance';
import { resolveMoveComponentTransformTarget } from '../../assembly/move-component/MoveComponentTransformTargetResolver';
import {
    resolveAssemblyComponentTransformLinkContext,
    type ResolvedAssemblyComponentTransformLinkContext,
} from '../../assembly/move-component/AssemblyComponentTransformLinkResolver';
import {
    createAssemblyComponentDrivenConstraintsForToolMode,
    type AssemblyComponentDrivenConstraintMap,
} from '../../assembly/move-component/AssemblyComponentTransformIntent';
import { AbstractEntityDragStrategy, EntityDragStrategyType } from './AbstractEntityDragStrategy';
import { CollisionDetector } from './CollisionDetector';
import { AssemblyDragEventName } from '../../eventBus/assemblyDragEvents';
import { useDispatchEvent } from '../../hooks/useEvent';

/**
 * 装配体零件拖拽工作模式。
 */
export enum AssemblyPartDragOperationMode {
    /** 平移模式。 */
    Move = 'move',
    /** 旋转模式。 */
    Rotate = 'rotate',
}

/**
 * 装配体 XYZ 锁定轴。
 */
type AssemblyAxisLock = 'x' | 'y' | 'z';

/**
 * 装配体零部件局部变换快照。
 */
interface AssemblyPartLocalTransformSnapshot {
    /** 局部位置。 */
    position: THREE.Vector3;
    /** 局部旋转。 */
    quaternion: THREE.Quaternion;
    /** 局部缩放。 */
    scale: THREE.Vector3;
    /** 捕获时的世界矩阵。 */
    worldMatrix: THREE.Matrix4;
}

/**
 * 装配体拖拽历史提交版本表。
 *
 * 设计说明：
 * 拖拽停止回调当前仍是同步生命周期，历史持久化可能晚于下一次用户操作完成。这里用 WeakMap 记录
 * 每个 Object3D 最近一次拖拽历史提交版本，失败回滚前必须确认没有新的拖拽会话接管该对象。
 */
const assemblyDragHistoryCommitVersions = new WeakMap<THREE.Object3D, number>();

/** 装配体拖拽调试日志开关，默认关闭以避免交互路径输出噪声。 */
const ASSEMBLY_PART_DRAG_DEBUG_ENABLED = false;

/**
 * 输出装配体拖拽调试信息。
 *
 * @param message 调试文案。
 */
function reportAssemblyPartDragDebug(message: string): void {
    if (!ASSEMBLY_PART_DRAG_DEBUG_ENABLED) {
        return;
    }
    globalThis.console?.warn(message);
}

/**
 * 推进对象的拖拽历史提交版本。
 *
 * @param object 被拖拽对象。
 * @returns 推进后的版本号。
 */
function advanceAssemblyDragHistoryCommitVersion(object: THREE.Object3D): number {
    const nextVersion = (assemblyDragHistoryCommitVersions.get(object) ?? 0) + 1;

    assemblyDragHistoryCommitVersions.set(object, nextVersion);

    return nextVersion;
}

/**
 * 判断对象是否为装配体零件拖拽策略。
 *
 * @param value 待判断的拖拽策略对象。
 * @returns 当对象为装配体零件拖拽策略时返回 `true`。
 */
export function isAssemblyPartDragStrategy(value: AbstractEntityDragStrategy): value is AssemblyPartDragStrategy {
    return value.strategyType === EntityDragStrategyType.AssemblyPart;
}

/**
 * 装配体零件拖拽策略。
 */
export class AssemblyPartDragStrategy extends AbstractEntityDragStrategy {
    /** 策略类型标识。 */
    public readonly strategyType = EntityDragStrategyType.AssemblyPart;

    /** 当前拖拽的零件或子装配实例根对象。 */
    private readonly targetObject: THREE.Object3D;

    /**
     * 当前拖拽目标是否已经解析到真实可写组件根。
     *
     * 设计说明：
     * 命令态 FeatureFace/FeatureLine/FeaturePoint 只能承载拓扑选择和高亮，不能承载 transform。
     * 这里保留一个显式解析状态，避免解析失败时退回写入代理对象。
     */
    private readonly transformTargetResolved: boolean;

    /** 当前拖拽需要联动的零部件对象组。 */
    private readonly linkedTransformObjects: THREE.Object3D[];

    
    /** 线性阵列同源旋转的共同锚点对象；普通零部件为空。 */
    private readonly linkedRotationAnchorObject: THREE.Object3D | null;
    /** Mate 刚性组合自由旋转时用于计算共同 bbox 中心的对象集合。 */
    private readonly linkedRotationPivotObjects: THREE.Object3D[];
    

    /** 拖拽参考平面。 */
    private dragPlane: THREE.Plane | null = null;

    /** 初始局部位置。 */
    private initialTargetPosition: THREE.Vector3 | null = null;

    /** 初始世界位置。 */
    private initialTargetWorldPosition: THREE.Vector3 | null = null;

    /** 拖拽起点在视平面上的交点。 */
    private dragStartIntersection: THREE.Vector3 | null = null;

    /** 当前拖拽工作模式。 */
    private operationMode: AssemblyPartDragOperationMode = AssemblyPartDragOperationMode.Move;

    /** 旋转起点鼠标 X 坐标。 */
    private rotateStartClientX = 0;

    /** 旋转起点鼠标 Y 坐标。 */
    private rotateStartClientY = 0;

    /** 旋转起始四元数。 */
    private initialTargetQuaternion: THREE.Quaternion | null = null;

    /** 拖拽开始前的组件实例快照，用于 mouseup 时生成一个模型事务。 */
    private initialComponentSnapshot: ComponentInstanceSnapshot | null = null;

    /** 拖拽开始前的联动组件实例快照集合。 */
    private readonly initialLinkedComponentSnapshotByObjectId = new Map<string, ComponentInstanceSnapshot>();

    /** Mate 实时求解涉及组件的拖拽初始快照。 */
    private readonly initialMateComponentSnapshotByComponentId = new Map<string, ComponentInstanceSnapshot>();

    /** Mate 实时求解涉及组件的最新快照。 */
    private readonly latestMateComponentSnapshotByComponentId = new Map<string, ComponentInstanceSnapshot>();

    /** 上一帧有效世界位置。 */
    private lastValidWorldPosition: THREE.Vector3 | null = null;

    /** 当前拖拽增量的世界位置基线。 */
    private readonly linkedWorldPositionBaselineById = new Map<string, THREE.Vector3>();

    /** 上一帧有效世界位置快照。 */
    private readonly linkedLastValidWorldPositionById = new Map<string, THREE.Vector3>();

    /** 当前拖拽增量的四元数基线。 */
    private readonly linkedQuaternionBaselineById = new Map<string, THREE.Quaternion>();

    /** 上一帧有效四元数快照。 */
    private readonly linkedLastValidQuaternionById = new Map<string, THREE.Quaternion>();

    /** 当前旋转增量的完整局部变换基线。 */
    private readonly linkedTransformBaselineById = new Map<string, AssemblyPartLocalTransformSnapshot>();

    /** 上一帧有效完整局部变换快照。 */
    private readonly linkedLastValidTransformById = new Map<string, AssemblyPartLocalTransformSnapshot>();

    /** 本次旋转拖拽使用的零部件自身世界包围盒中心。 */
    private readonly linkedRotationPivotWorldById = new Map<string, THREE.Vector3>();

    /** 旋转中心包围盒计算缓存。 */
    private readonly rotateBoundingBoxBuffer = new THREE.Box3();

    /** 联动组包围盒中心缓存。 */
    private readonly rotateLinkedBoundsCenterBuffer = new THREE.Vector3();

    /** 世界旋转增量四元数缓存。 */
    private readonly rotateWorldDeltaQuaternionBuffer = new THREE.Quaternion();

    /** 世界旋转增量矩阵缓存。 */
    private readonly rotateWorldDeltaMatrixBuffer = new THREE.Matrix4();

    /** 已提交 Mate 剩余转轴投影器，视口拖拽和数值旋转共用同一套自由度解释。 */
    private readonly constrainedRotationProjector = new AssemblyConstrainedRotationProjector();

    /** 当前旋转拖拽会话缓存的 Mate 剩余转轴。 */
    private constrainedRotationAxis: AssemblyMateComponentRotationAxis | null = null;

    /**
     * 剩余轴不支持鼠标方向时，可整体刚性旋转的整个连通装配对象。
     *
     * 说明：仅当选中件所在的已提交配合连通装配全浮动时非空；空表示无法整体自由旋转，保持约束旋转。
     */
    private wholeGroupRotationObjects: THREE.Object3D[] = [];

    /** 本次旋转拖拽的“约束/整组”模式是否已锁定，避免拖拽过程中来回切换造成抖动。 */
    private rotationModeDecisionLocked = false;

    /** 剩余轴对齐判定的方向夹角余弦阈值（~45° 内视为沿剩余轴）。 */
    private static readonly ROTATION_RESIDUAL_ALIGNMENT_COS = Math.cos(Math.PI / 4);

    /** 判定拖拽方向时可忽略的最小增量转角对应的半角正弦。 */
    private static readonly ROTATION_MIN_DRAG_SIN_HALF = Math.sin(1e-4 / 2);

    /** 整组升级判定复用的鼠标旋转轴缓存。 */
    private readonly wholeGroupDragAxisBuffer = new THREE.Vector3();

    /** 整组升级判定复用的剩余轴缓存。 */
    private readonly wholeGroupResidualAxisBuffer = new THREE.Vector3();

    /** 旋转中心平移矩阵缓存。 */
    private readonly rotatePivotToWorldMatrixBuffer = new THREE.Matrix4();

    /** 旋转中心反向平移矩阵缓存。 */
    private readonly rotateWorldToPivotMatrixBuffer = new THREE.Matrix4();

    /** 旋转后世界矩阵缓存。 */
    private readonly rotateNextWorldMatrixBuffer = new THREE.Matrix4();

    /** 父级世界逆矩阵缓存。 */
    private readonly rotateParentWorldInverseMatrixBuffer = new THREE.Matrix4();

    /** 旋转后局部矩阵缓存。 */
    private readonly rotateNextLocalMatrixBuffer = new THREE.Matrix4();

    /** 已脱离 source Mesh 的世界包围盒换算缓存。 */
    private readonly effectiveSourceBoundsBuffer = new THREE.Box3();

    /** AlongAssemblyAxis 模式下本次拖拽锁定的装配体轴。 */
    private lockedAssemblyAxis: AssemblyAxisLock | null = null;

    /** 碰撞检测器。 */
    private readonly collisionDetector: CollisionDetector = new CollisionDetector();

    /** 本次拖拽是否启用碰撞检测。 */
    private collisionEnabled = false;

    /** 本次拖拽发生碰撞时是否停止。 */
    private collisionStopAtCollision = false;

    /** 当前沿实体参考约束。 */
    private readonly alongEntityReference: MoveAlongEntityResolvedReference | null;

    /** 由入口事件显式指定的拖拽模式；右键拖动必须固定为旋转，不依赖当前工具栏模式。 */
    private readonly operationModeOverride: AssemblyPartDragOperationMode | null;

    /** 沿实体线性方向计算缓存。 */
    private readonly alongEntityDirectionBuffer = new THREE.Vector3();

    /** 沿实体平面法向计算缓存。 */
    private readonly alongEntityPlaneNormalBuffer = new THREE.Vector3();

    /** 沿实体位移裁剪结果缓存。 */
    private readonly alongEntityConstrainedDeltaBuffer = new THREE.Vector3();

    /**
     * 构造函数。
     *
     * @param renderer 渲染器实例。
     * @param targetObject 当前拖拽的零件或子装配实例对象。
     * @param alongEntityReference 当前沿实体参考约束；非沿实体模式时传入 `null`。
     */
    public constructor(
        renderer: Viewer,
        targetObject: THREE.Object3D,
        alongEntityReference: MoveAlongEntityResolvedReference | null = null,
        operationModeOverride: AssemblyPartDragOperationMode | null = null,
    ) {
        super(renderer);
        const transformRoot = resolveMoveComponentTransformTarget(targetObject as never, renderer as never);
        const transformLinkContext = transformRoot
            ? this.resolveTransformLinkContext(transformRoot)
            : {
                linkedObjects: [],
                rotationAnchorObject: null,
                rotationPivotObjects: [],
            };
        this.transformTargetResolved = Boolean(transformRoot);
        this.targetObject = transformRoot ?? targetObject;
        this.linkedTransformObjects = transformLinkContext.linkedObjects;
        
        this.linkedRotationAnchorObject = transformLinkContext.rotationAnchorObject;
        this.linkedRotationPivotObjects = transformLinkContext.rotationPivotObjects;
        
        this.alongEntityReference = alongEntityReference
            ? {
                ...alongEntityReference,
                anchorWorld: alongEntityReference.anchorWorld.clone(),
                directionWorld: alongEntityReference.directionWorld?.clone() ?? null,
                planeNormalWorld: alongEntityReference.planeNormalWorld?.clone() ?? null,
            }
            : null;
        this.operationModeOverride = operationModeOverride;
    }

    /**
     * 获取当前拖拽目标对象。
     *
     * @returns 拖拽中的零件或子装配实例对象。
     */
    public getTargetObject(): THREE.Object3D {
        return this.targetObject;
    }

    /**
     * 获取当前拖拽需要联动的对象组。
     *
     * @returns 本次拖拽会一起变换的对象集合。
     */
    public getLinkedTransformObjects(): readonly THREE.Object3D[] {
        return this.linkedTransformObjects;
    }

    /**
     * 获取当前拖拽工作模式。
     *
     * @returns 拖拽工作模式。
     */
    public getOperationMode(): AssemblyPartDragOperationMode {
        return this.operationMode;
    }

    /**
     * 开始零件拖拽。
     *
     * @param payload 原始鼠标事件载荷。
     */
    public start(payload: RawPointerEventPayload): void {
        if (this.renderer.moveComponentController?.isAssemblyTransformHistoryCommitPending()) {
            reportAssemblyPartDragDebug('前一笔装配体拖拽历史尚未提交完成，已拒绝新的拖拽。');
            return;
        }

        if (!this.canTransformTargetObject()) {
            reportAssemblyPartDragDebug(
                `目标零部件不是明确浮动状态，已拒绝移动或旋转。objectId=${this.targetObject.uuid}`,
            );
            return;
        }

        this.dragPreviewOwner = this.targetObject;
        this.disableCameraControlsForDrag();
        advanceAssemblyDragHistoryCommitVersion(this.targetObject);

        this.initialTargetPosition = this.targetObject.position.clone();
        this.initialTargetWorldPosition = this.getEffectiveWorldPosition(
            this.targetObject,
            new THREE.Vector3(),
        );
        this.lastValidWorldPosition = this.initialTargetWorldPosition.clone();
        this.captureLinkedWorldPositionState(this.linkedWorldPositionBaselineById);
        this.captureLinkedWorldPositionState(this.linkedLastValidWorldPositionById);
        this.captureLinkedComponentSnapshots(this.initialLinkedComponentSnapshotByObjectId);
        this.initialMateComponentSnapshotByComponentId.clear();
        this.latestMateComponentSnapshotByComponentId.clear();
        this.initialComponentSnapshot = createComponentInstanceSnapshotFromObject({
            object: this.targetObject as unknown as WebGpuObject3D,
        });

        const toolMode = this.renderer.moveComponentController?.getToolMode() ?? AssemblyTransformToolMode.Move;
        this.operationMode = this.operationModeOverride ?? (toolMode === AssemblyTransformToolMode.Rotate
            ? AssemblyPartDragOperationMode.Rotate
            : AssemblyPartDragOperationMode.Move);
        this.constrainedRotationAxis = this.operationMode === AssemblyPartDragOperationMode.Rotate
            ? this.resolveConstrainedRotationAxisForActiveDrag()
            : null;
        // 预解析“剩余轴不支持鼠标方向时可整体刚性旋转的整个连通装配”。仅在存在剩余轴约束、且不是线性阵列
        // （无共同旋转锚点）时才需要判定升级；每次拖拽重置模式锁。
        this.rotationModeDecisionLocked = false;
        this.wholeGroupRotationObjects = this.constrainedRotationAxis && !this.linkedRotationAnchorObject
            ? this.resolveWholeGroupRotationObjects()
            : [];

        
        const collisionConfig = this.renderer.moveComponentController?.getCollisionDetectionConfig?.() ?? null;
        this.collisionEnabled = collisionConfig?.enabled ?? false;
        this.collisionStopAtCollision = collisionConfig?.stopAtCollision ?? false;

        if (this.collisionEnabled && collisionConfig) {
            const dragObjects = collisionConfig.draggedPartOnly
                ? [this.targetObject]
                : this.linkedTransformObjects;
            const sceneObjects = this.resolveCollisionSceneObjects(collisionConfig);
            /**
             * 碰撞检测阶段只缓存“已有加速树”的真实几何对象。
             *
             * 说明：
             * 1. BVH 生命周期已统一收口在 `raycast-acceleration`；
             * 2. 拖拽开始时这里只做候选缓存，不允许再生成或更新 `boundsTree`；
             * 3. 未接入加速树的对象会在 `CollisionDetector` 内被自动跳过。
             */
            this.collisionDetector.init(dragObjects, sceneObjects, this.renderer);
        }
        

        
        if (this.operationMode === AssemblyPartDragOperationMode.Move) {
            const cameraForward = new THREE.Vector3();
            this.renderer.camera.getWorldDirection(cameraForward);
            if (cameraForward.lengthSq() > 0 && this.initialTargetWorldPosition) {
                this.dragPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(
                    cameraForward.normalize(),
                    this.initialTargetWorldPosition,
                );
            }

            if (this.dragPlane && this.updateMouseNdc(payload.clientX, payload.clientY)) {
                this.raycaster.setFromCamera(this.mouseNdc, this.renderer.camera);
                const startIntersection = new THREE.Vector3();
                if (this.raycaster.ray.intersectPlane(this.dragPlane, startIntersection)) {
                    this.dragStartIntersection = startIntersection;
                }
            }
        } else {
            this.rotateStartClientX = payload.clientX;
            this.rotateStartClientY = payload.clientY;
            this.initialTargetQuaternion = this.targetObject.quaternion.clone();
            this.captureLinkedQuaternionState(this.linkedQuaternionBaselineById);
            this.captureLinkedQuaternionState(this.linkedLastValidQuaternionById);
            this.captureLinkedTransformState(this.linkedTransformBaselineById);
            this.captureLinkedTransformState(this.linkedLastValidTransformById);
            this.captureLinkedRotationPivotWorldState(this.linkedRotationPivotWorldById);
        }

        // 通知 UI 层（AssemblyToolbarTab）当前正在拖拽的零部件，高亮对应 tab。
        useDispatchEvent(AssemblyDragEventName.DragStarted, {
            targetObjectUuid: this.targetObject.uuid,
        });
    }

    /**
     * 更新零件拖拽位置。
     *
     * @param payload 原始鼠标事件载荷。
     */
    public update(payload: RawPointerEventPayload): void {
        if (!this.canTransformTargetObject()) {
            return;
        }

        if (!this.initialTargetPosition || !this.initialTargetWorldPosition) {
            return;
        }

        if (this.operationMode === AssemblyPartDragOperationMode.Rotate) {
            this.updateRotateByPointerDelta(payload);
            return;
        }

        if (!this.dragPlane || !this.dragStartIntersection) {
            return;
        }

        if (!this.updateMouseNdc(payload.clientX, payload.clientY)) {
            return;
        }

        this.raycaster.setFromCamera(this.mouseNdc, this.renderer.camera);
        const intersection = new THREE.Vector3();
        if (!this.raycaster.ray.intersectPlane(this.dragPlane, intersection)) {
            return;
        }

        let delta = intersection.clone().sub(this.dragStartIntersection);

        const moveInputMode = this.renderer.moveComponentController?.getMoveInputMode?.() ?? MoveComponentInputMode.FreeDrag;
        
        if (moveInputMode === MoveComponentInputMode.AlongEntity) {
            delta = this.applyAlongEntityConstraint(delta);
        } else if (moveInputMode === MoveComponentInputMode.AlongAssemblyAxis) {
            
            const assemblyWorldQuat = new THREE.Quaternion();
            this.renderer.scene.getWorldQuaternion(assemblyWorldQuat);
            const assemblyWorldQuatInv = assemblyWorldQuat.clone().invert();

            const localDelta = delta.clone().applyQuaternion(assemblyWorldQuatInv);
            const lockedAxis = this.resolveLockedAssemblyAxis(localDelta);
            this.applyLockedAssemblyAxis(localDelta, lockedAxis);

            delta = localDelta.applyQuaternion(assemblyWorldQuat);
        } else {
            this.lockedAssemblyAxis = null;
        }
        

        const previousWorldPosition = this.lastValidWorldPosition
            ? this.lastValidWorldPosition.clone()
            : this.initialTargetWorldPosition.clone();

        const nextWorldPosition = this.initialTargetWorldPosition.clone().add(delta);
        this.applyWorldDeltaToLinkedObjects(delta);

        
        const hasMoveCollision = this.collisionEnabled && this.collisionDetector.detect(this.targetObject);
        if (hasMoveCollision && this.collisionStopAtCollision) {
            this.restoreLinkedWorldPositions(this.linkedLastValidWorldPositionById);

            this.lastValidWorldPosition = previousWorldPosition.clone();
            this.initialTargetWorldPosition.copy(previousWorldPosition);
            this.captureLinkedWorldPositionState(this.linkedWorldPositionBaselineById);
            this.dragStartIntersection = intersection.clone();

            this.recomputeDependentAssemblyDatumReferences();
            this.requestRenderAfterTransform();
            return;
        }
        

        if (!this.solveCommittedMatesForActiveDrag()) {
            this.restoreLinkedWorldPositions(this.linkedLastValidWorldPositionById);
            this.initialTargetWorldPosition.copy(previousWorldPosition);
            this.captureLinkedWorldPositionState(this.linkedWorldPositionBaselineById);
            this.dragStartIntersection = intersection.clone();
            this.recomputeDependentAssemblyDatumReferences();
            this.requestRenderAfterTransform();
            return;
        }

        const solvedWorldPosition = this.getEffectiveWorldPosition(this.targetObject, nextWorldPosition);
        this.lastValidWorldPosition = solvedWorldPosition.clone();
        this.initialTargetWorldPosition.copy(solvedWorldPosition);
        this.captureLinkedWorldPositionState(this.linkedWorldPositionBaselineById);
        this.captureLinkedWorldPositionState(this.linkedLastValidWorldPositionById);
        this.dragStartIntersection = intersection.clone();
        this.recomputeDependentAssemblyDatumReferences();
        this.requestRenderAfterTransform();
    }

    /**
     * 按鼠标屏幕位移更新旋转。
     *
     * @param payload 原始鼠标事件载荷。
     */
    private updateRotateByPointerDelta(payload: RawPointerEventPayload): void {
        if (!this.canTransformTargetObject()) {
            return;
        }

        if (!this.initialTargetQuaternion) {
            return;
        }

        const deltaX = payload.clientX - this.rotateStartClientX;
        const deltaY = payload.clientY - this.rotateStartClientY;

        const rotationPerPixel = 0.25;
        const yawDeg = deltaX * rotationPerPixel;
        // 浏览器 clientY 向下递增；SolidWorks 的旋转零部件竖向拖拽使用屏幕位移同向映射，不能反向取值。
        const pitchDeg = deltaY * rotationPerPixel;

        const cameraUp = new THREE.Vector3(0, 1, 0).applyQuaternion(this.renderer.camera.quaternion).normalize();
        const cameraRight = new THREE.Vector3(1, 0, 0).applyQuaternion(this.renderer.camera.quaternion).normalize();

        const yawQuaternion = new THREE.Quaternion().setFromAxisAngle(cameraUp, THREE.MathUtils.degToRad(yawDeg));
        const pitchQuaternion = new THREE.Quaternion().setFromAxisAngle(cameraRight, THREE.MathUtils.degToRad(pitchDeg));

        this.applyRotationToLinkedObjects(yawQuaternion, pitchQuaternion);

        
        const hasRotateCollision = this.collisionEnabled && this.collisionDetector.detect(this.targetObject);
        if (hasRotateCollision && this.collisionStopAtCollision) {
            this.restoreLinkedTransforms(this.linkedLastValidTransformById);

            this.initialTargetQuaternion.copy(this.targetObject.quaternion);
            this.captureLinkedQuaternionState(this.linkedQuaternionBaselineById);
            this.captureLinkedTransformState(this.linkedTransformBaselineById);
            this.rotateStartClientX = payload.clientX;
            this.rotateStartClientY = payload.clientY;

            this.recomputeDependentAssemblyDatumReferences();
            this.requestRenderAfterTransform();
            return;
        }
        

        if (!this.solveCommittedMatesForActiveDrag()) {
            this.restoreLinkedTransforms(this.linkedLastValidTransformById);
            this.initialTargetQuaternion.copy(this.targetObject.quaternion);
            this.captureLinkedQuaternionState(this.linkedQuaternionBaselineById);
            this.captureLinkedTransformState(this.linkedTransformBaselineById);
            this.rotateStartClientX = payload.clientX;
            this.rotateStartClientY = payload.clientY;
            this.recomputeDependentAssemblyDatumReferences();
            this.requestRenderAfterTransform();
            return;
        }

        this.initialTargetQuaternion.copy(this.targetObject.quaternion);
        this.captureLinkedQuaternionState(this.linkedQuaternionBaselineById);
        this.captureLinkedQuaternionState(this.linkedLastValidQuaternionById);
        this.captureLinkedTransformState(this.linkedTransformBaselineById);
        this.captureLinkedTransformState(this.linkedLastValidTransformById);
        this.rotateStartClientX = payload.clientX;
        this.rotateStartClientY = payload.clientY;
        this.recomputeDependentAssemblyDatumReferences();
        this.requestRenderAfterTransform();
    }

    
    /**
     * 解析本次拖拽参与碰撞检查的场景候选对象。
     *
     * 设计说明：
     * 1. “所有零部件之间”沿用装配体全局候选，但排除被拖动对象子树；
     * 2. “这些零部件之间”只读取 PropertyManager 选择框中保存的零部件 ID；
     * 3. 候选对象在进入 CollisionDetector 前去重并再次排除拖拽对象，避免自碰撞误判。
     *
     * @param config 碰撞检查配置。
     * @returns 可参与碰撞检查的场景对象。
     */
    private resolveCollisionSceneObjects(config: MoveComponentCollisionDetectionConfig): THREE.Object3D[] {
        if (config.scope === MoveComponentCollisionCheckScope.SelectedComponents) {
            const selectedObjects = config.componentObjectIds
                .map((objectId) => this.renderer.findSceneObjectById(objectId))
                .filter((object): object is THREE.Object3D => Boolean(object))
                .map((object) => resolveMoveComponentTransformTarget(object as never, this.renderer as never) ?? object);

            return this.filterUniqueCollisionSceneObjects(selectedObjects);
        }

        const sceneObjects: THREE.Object3D[] = [];
        this.renderer.scene?.traverse((object) => {
            if (!(object instanceof THREE.Mesh)) {
                return;
            }
            sceneObjects.push(object);
        });

        return this.filterUniqueCollisionSceneObjects(sceneObjects);
    }

    /**
     * 对碰撞候选对象去重并排除拖拽对象自身。
     *
     * @param sceneObjects 原始候选对象。
     * @returns 可用于碰撞检测的候选对象。
     */
    private filterUniqueCollisionSceneObjects(sceneObjects: readonly THREE.Object3D[]): THREE.Object3D[] {
        const sceneObjectMap = new Map<string, THREE.Object3D>();
        sceneObjects.forEach((sceneObject) => {
            if (this.isObjectWithinAnyTargetSubtree(sceneObject, this.linkedTransformObjects)) {
                return;
            }
            sceneObjectMap.set(sceneObject.uuid, sceneObject);
        });
        return [...sceneObjectMap.values()];
    }
    

    /**
     * 判断对象是否位于目标对象子树中。
     *
     * @param object 待判断对象。
     * @param root 根对象。
     * @returns 若 object 位于 root 子树中则返回 `true`。
     */
    private isDescendantOfTarget(object: THREE.Object3D, root: THREE.Object3D): boolean {
        let current: THREE.Object3D | null = object.parent;
        while (current) {
            if (current === root) {
                return true;
            }
            current = current.parent;
        }
        return false;
    }

    /**
     * 判断对象是否位于任一拖拽目标子树中。
     *
     * @param object 待判断对象。
     * @param roots 拖拽目标根对象集合。
     * @returns 若 object 位于任一目标子树中则返回 `true`。
     */
    private isObjectWithinAnyTargetSubtree(object: THREE.Object3D, roots: readonly THREE.Object3D[]): boolean {
        return roots.some((root) => object === root || this.isDescendantOfTarget(object, root));
    }

    
    /**
     * 判断当前拖拽目标是否允许被移动或旋转。
     *
     * 设计说明：
     * 1. 视口直接拖拽会绕过 MoveService / RotateService，必须在策略入口复用同一套固定/浮动规则；
     * 2. 只有未明确设置 `metadata.importedModel.hierarchy.meta.isFixed === true` 的装配体零部件才允许变换；
     * 3. 固定零部件会被拒绝，缺少 imported-model 固定状态的对象按浮动处理。
     *
     * @returns 目标对象允许变换时返回 `true`。
     */
    private canTransformTargetObject(): boolean {
        if (!this.transformTargetResolved) {
            return false;
        }
        return this.linkedTransformObjects.every((object) => AssemblyComponentFixationService.canTransformSceneObject(object));
    }
    

    /**
     * 判断组件是否已由拖拽联动对象负责记录历史。
     *
     * @param componentInstanceId 组件实例 ID。
     * @returns 是否已存在拖拽联动历史基线。
     */
    private isLinkedTransformComponent(componentInstanceId: string): boolean {
        return [...this.initialLinkedComponentSnapshotByObjectId.values()]
            .some((snapshot) => snapshot.componentInstanceId === componentInstanceId);
    }

    /**
     * 解析当前旋转拖拽可使用的 Mate 剩余转轴。
     *
     * @returns 可投影视口旋转输入的剩余转轴；不存在转动副自由度时返回 null。
     */
    private resolveConstrainedRotationAxisForActiveDrag(): AssemblyMateComponentRotationAxis | null {
        if (!this.initialComponentSnapshot) {
            return null;
        }

        return this.renderer.assemblyMateController?.resolveComponentRotationAxisForTransform?.({
            componentInstanceId: this.initialComponentSnapshot.componentInstanceId,
        }) ?? null;
    }

    /**
     * 构建当前拖拽传递给 Mate 图求解器的用户意图约束。
     *
     * @returns 旋转拖拽的 driven 约束；移动拖拽返回 undefined。
     */
    private resolveDrivenComponentConstraintsForActiveDrag(): AssemblyComponentDrivenConstraintMap | undefined {
        if (!this.initialComponentSnapshot) {
            return undefined;
        }

        return createAssemblyComponentDrivenConstraintsForToolMode(
            this.initialComponentSnapshot.componentInstanceId,
            this.operationMode === AssemblyPartDragOperationMode.Rotate
                ? AssemblyTransformToolMode.Rotate
                : AssemblyTransformToolMode.Move,
        );
    }

    /**
     * 针对当前视口直接拖拽重求解已提交配合。
     *
     * @param options 求解选项。
     * @returns 当前拖拽位姿是否能被已提交配合接受。
     */
    private solveCommittedMatesForActiveDrag(
        options: { readonly updateDocumentStatus?: boolean } = {},
    ): boolean {
        if (!this.initialComponentSnapshot) {
            return true;
        }

        const drivenComponentConstraints = this.resolveDrivenComponentConstraintsForActiveDrag();
        const solveResult = this.renderer.assemblyMateController?.solveCommittedMatesAfterComponentTransform({
            anchorComponentInstanceId: this.initialComponentSnapshot.componentInstanceId,
            ...(drivenComponentConstraints ? { drivenComponentConstraints } : {}),
            /**
             * 直接拖拽会在高频路径中反复求解，这里默认只同步场景位姿。
             * mouseup 提交时会显式写入诊断状态，避免预览期触发持久化保存。
             */
            updateDocumentStatus: options.updateDocumentStatus ?? false,
        }) ?? null;
        if (!solveResult) {
            return true;
        }
        if (solveResult.status !== MateSolveStatus.Solved) {
            return false;
        }

        solveResult.beforeSnapshots.forEach((beforeSnapshot) => {
            if (this.isLinkedTransformComponent(beforeSnapshot.componentInstanceId)) {
                return;
            }
            if (!this.initialMateComponentSnapshotByComponentId.has(beforeSnapshot.componentInstanceId)) {
                this.initialMateComponentSnapshotByComponentId.set(beforeSnapshot.componentInstanceId, beforeSnapshot);
            }
        });
        solveResult.afterSnapshots.forEach((afterSnapshot) => {
            if (this.isLinkedTransformComponent(afterSnapshot.componentInstanceId)) {
                return;
            }
            this.latestMateComponentSnapshotByComponentId.set(afterSnapshot.componentInstanceId, afterSnapshot);
        });
        return true;
    }

    /**
     * 解析当前拖拽对象对应的联动对象组。
     *
     * @param targetObject 当前拖拽目标对象。
     * @returns 本次需要一起变换的对象组。
     */
    private resolveTransformLinkContext(targetObject: THREE.Object3D): ResolvedAssemblyComponentTransformLinkContext {
        return resolveAssemblyComponentTransformLinkContext(this.renderer as never, targetObject);
    }

    /**
     * 解析组件对象用于局部/世界变换换算的有效父级。
     *
     * 实例投影会把原 source Mesh 从物理场景树移除，但该 Mesh 的 position/quaternion/scale
     * 仍然定义在脱离前父级的局部坐标系中。只有渲染父链被移除，业务父级关系没有改变。
     *
     * @param object 当前组件变换对象。
     * @returns 业务有效父级；世界根返回 null。
     */
    private resolveEffectiveTransformParent(object: THREE.Object3D): THREE.Object3D | null {
        if (object instanceof THREE.Mesh) {
            const originalParent = this.renderer.resolveImportedModelSourceMeshOriginalParent?.(object) ?? null;
            if (originalParent) {
                return originalParent;
            }
        }
        return object.parent;
    }

    /**
     * 刷新组件对象的有效世界矩阵。
     *
     * @param object 当前组件变换对象。
     */
    private updateEffectiveWorldMatrix(object: THREE.Object3D): void {
        if (
            object instanceof THREE.Mesh
            && this.renderer.prepareImportedModelSourceMeshForSpatialRead?.(object) === true
        ) {
            return;
        }
        object.updateWorldMatrix(true, false);
    }

    /**
     * 读取组件对象的有效世界位置。
     *
     * @param object 当前组件变换对象。
     * @param target 接收世界位置的复用向量。
     * @returns target。
     */
    private getEffectiveWorldPosition(
        object: THREE.Object3D,
        target: THREE.Vector3,
    ): THREE.Vector3 {
        this.updateEffectiveWorldMatrix(object);
        return target.setFromMatrixPosition(object.matrixWorld);
    }

    /**
     * 把世界矩阵写回组件对象的业务局部坐标系。
     *
     * @param object 当前组件变换对象。
     * @param worldMatrix 目标世界矩阵。
     */
    private applyEffectiveWorldMatrix(
        object: THREE.Object3D,
        worldMatrix: THREE.Matrix4,
    ): void {
        const effectiveParent = this.resolveEffectiveTransformParent(object);
        if (effectiveParent) {
            effectiveParent.updateWorldMatrix(true, false);
            this.rotateParentWorldInverseMatrixBuffer.copy(effectiveParent.matrixWorld).invert();
            this.rotateNextLocalMatrixBuffer.multiplyMatrices(
                this.rotateParentWorldInverseMatrixBuffer,
                worldMatrix,
            );
        } else {
            this.rotateNextLocalMatrixBuffer.copy(worldMatrix);
        }

        this.rotateNextLocalMatrixBuffer.decompose(object.position, object.quaternion, object.scale);
        this.updateEffectiveWorldMatrix(object);
    }

    /**
     * 捕获当前联动对象组的世界位置快照。
     *
     * @param targetMap 目标快照映射。
     */
    private captureLinkedWorldPositionState(targetMap: Map<string, THREE.Vector3>): void {
        targetMap.clear();
        this.linkedTransformObjects.forEach((object) => {
            targetMap.set(object.uuid, this.getEffectiveWorldPosition(object, new THREE.Vector3()));
        });
    }

    /**
     * 捕获当前联动组件实例快照。
     *
     * @param targetMap 快照目标映射。
     */
    private captureLinkedComponentSnapshots(targetMap: Map<string, ComponentInstanceSnapshot>): void {
        targetMap.clear();
        this.linkedTransformObjects.forEach((object) => {
            targetMap.set(object.uuid, createComponentInstanceSnapshotFromObject({
                object: object as unknown as WebGpuObject3D,
            }));
        });
    }

    /**
     * 按当前基线对联动对象组应用统一世界位移。
     *
     * @param deltaWorld 世界坐标位移增量。
     */
    private applyWorldDeltaToLinkedObjects(deltaWorld: THREE.Vector3): void {
        this.linkedTransformObjects.forEach((object) => {
            const baselineWorldPosition = this.linkedWorldPositionBaselineById.get(object.uuid);
            if (!baselineWorldPosition) {
                return;
            }

            const nextWorldPosition = baselineWorldPosition.clone().add(deltaWorld);
            const effectiveParent = this.resolveEffectiveTransformParent(object);
            if (effectiveParent) {
                const nextLocalPosition = effectiveParent.worldToLocal(nextWorldPosition.clone());
                object.position.copy(nextLocalPosition);
            } else {
                object.position.copy(nextWorldPosition);
            }
            this.updateEffectiveWorldMatrix(object);
        });
    }

    /**
     * 从快照恢复联动对象组的世界位置。
     *
     * @param sourceMap 世界位置快照映射。
     */
    private restoreLinkedWorldPositions(sourceMap: ReadonlyMap<string, THREE.Vector3>): void {
        this.linkedTransformObjects.forEach((object) => {
            const worldPosition = sourceMap.get(object.uuid);
            if (!worldPosition) {
                return;
            }

            const effectiveParent = this.resolveEffectiveTransformParent(object);
            if (effectiveParent) {
                const previousLocalPosition = effectiveParent.worldToLocal(worldPosition.clone());
                object.position.copy(previousLocalPosition);
            } else {
                object.position.copy(worldPosition);
            }
            this.updateEffectiveWorldMatrix(object);
        });
    }

    /**
     * 捕获当前联动对象组的四元数快照。
     *
     * @param targetMap 目标快照映射。
     */
    private captureLinkedQuaternionState(targetMap: Map<string, THREE.Quaternion>): void {
        targetMap.clear();
        this.linkedTransformObjects.forEach((object) => {
            targetMap.set(object.uuid, object.quaternion.clone());
        });
    }

    /**
     * 捕获当前联动对象组的完整局部变换快照。
     *
     * @param targetMap 目标快照映射。
     */
    private captureLinkedTransformState(targetMap: Map<string, AssemblyPartLocalTransformSnapshot>): void {
        targetMap.clear();
        this.linkedTransformObjects.forEach((object) => {
            this.updateEffectiveWorldMatrix(object);
            targetMap.set(object.uuid, {
                position: object.position.clone(),
                quaternion: object.quaternion.clone(),
                scale: object.scale.clone(),
                worldMatrix: object.matrixWorld.clone(),
            });
        });
    }

    /**
     * 捕获当前旋转拖拽使用的零部件自身包围盒中心。
     *
     * @param targetMap 目标旋转中心映射。
     */
    private captureLinkedRotationPivotWorldState(targetMap: Map<string, THREE.Vector3>): void {
        targetMap.clear();
        
        const sharedRotationPivotWorld = this.resolveLinkedRotationAnchorPivotWorld();
        this.linkedTransformObjects.forEach((object) => {
            targetMap.set(
                object.uuid,
                sharedRotationPivotWorld?.clone() ?? this.resolveRotationPivotWorld(object),
            );
        });
        
    }

    
    /**
     * 解析联动旋转对象组的共同世界旋转中心。
     *
     * 设计说明：
     * 线性阵列 A1/A2/A3 属于同一源链路时，旋转任意实例都应绕源零部件 A1 的中心旋转；
     * 多重 Mate 形成的浮动刚性组没有单一内部转轴语义，使用整组可见 bbox 中心，避免绕远处内部孔轴公转。
     *
     * @returns 共同旋转中心；无阵列锚点或 Mate 刚性组时返回 `null`。
     */
    private resolveLinkedRotationAnchorPivotWorld(): THREE.Vector3 | null {
        if (this.linkedRotationAnchorObject) {
            return this.resolveRotationPivotWorld(this.linkedRotationAnchorObject);
        }

        return this.resolveLinkedRotationPivotObjectsCenterWorld();
    }

    /**
     * 解析 Mate 刚性组合的世界 bbox 中心。
     *
     * @returns 刚性组合存在有效可见包围盒时返回中心，否则返回 `null`。
     */
    private resolveLinkedRotationPivotObjectsCenterWorld(): THREE.Vector3 | null {
        if (this.linkedRotationPivotObjects.length <= 1) {
            return null;
        }

        this.rotateBoundingBoxBuffer.makeEmpty();
        this.linkedRotationPivotObjects.forEach((object) => {
            this.expandBoundsByBusinessObject(this.rotateBoundingBoxBuffer, object);
        });
        if (this.rotateBoundingBoxBuffer.isEmpty()) {
            return null;
        }

        return this.rotateBoundingBoxBuffer.getCenter(this.rotateLinkedBoundsCenterBuffer).clone();
    }
    

    /**
     * 解析零部件自身世界包围盒中心。
     *
     * 设计说明：
     * 1. SOLIDWORKS 的“旋转零部件”围绕组件自身包围盒体积中心旋转；
     * 2. 组件原点经常来自导入文件或装配层级，不一定落在可见几何中心；
     * 3. 空组件没有可见包围盒时退回组件世界位置，避免生成无效矩阵污染拖拽链路。
     *
     * @param object 零部件变换根对象。
     * @returns 世界空间旋转中心。
     */
    private resolveRotationPivotWorld(object: THREE.Object3D): THREE.Vector3 {
        this.rotateBoundingBoxBuffer.makeEmpty();
        this.expandBoundsByBusinessObject(this.rotateBoundingBoxBuffer, object);
        if (!this.rotateBoundingBoxBuffer.isEmpty()) {
            return this.rotateBoundingBoxBuffer.getCenter(new THREE.Vector3());
        }

        return this.getEffectiveWorldPosition(object, new THREE.Vector3());
    }

    /**
     * 把业务对象或其已脱离主场景树的实例 source Mesh 合并到世界包围盒。
     *
     * @param bounds 接收世界包围盒的复用对象。
     * @param object occurrence 根、装配祖先或普通场景对象。
     */
    private expandBoundsByBusinessObject(
        bounds: THREE.Box3,
        object: THREE.Object3D,
    ): void {
        const sourceMeshes = this.renderer.listImportedModelInstancedSourceMeshes?.(object) ?? [];
        if (sourceMeshes.length === 0) {
            this.updateEffectiveWorldMatrix(object);
            bounds.expandByObject(object);
            return;
        }

        sourceMeshes.forEach((sourceMesh) => {
            this.updateEffectiveWorldMatrix(sourceMesh);
            if (!sourceMesh.geometry.boundingBox) {
                sourceMesh.geometry.computeBoundingBox();
            }
            if (!sourceMesh.geometry.boundingBox) {
                return;
            }
            /**
             * `Box3.expandByObject()` 会再次调用 sourceMesh.updateWorldMatrix()，把已脱离 Mesh
             * 的有效世界矩阵覆盖成无父级局部矩阵，因此这里直接变换几何包围盒。
             */
            this.effectiveSourceBoundsBuffer
                .copy(sourceMesh.geometry.boundingBox)
                .applyMatrix4(sourceMesh.matrixWorld);
            bounds.union(this.effectiveSourceBoundsBuffer);
        });
    }

    /**
     * 按当前四元数基线对联动对象组应用统一旋转增量。
     *
     * @param yawQuaternion 绕相机上方向的旋转增量。
     * @param pitchQuaternion 绕相机右方向的旋转增量。
     */
    private applyRotationToLinkedObjects(
        yawQuaternion: THREE.Quaternion,
        pitchQuaternion: THREE.Quaternion,
    ): void {
        const worldDeltaQuaternion = this.rotateWorldDeltaQuaternionBuffer
            .copy(pitchQuaternion)
            .multiply(yawQuaternion)
            .normalize();
        // 首个有效转动帧判定：鼠标方向不沿剩余轴时，升级为整个连通装配整体刚性旋转。
        this.maybeLockWholeGroupRotationMode(worldDeltaQuaternion);
        if (this.constrainedRotationAxis) {
            const signedAngle = this.constrainedRotationProjector.projectQuaternionDeltaToAxisAngle(
                worldDeltaQuaternion,
                this.constrainedRotationAxis,
            );
            if (!this.constrainedRotationProjector.buildAxisRotationDeltaMatrix(
                this.constrainedRotationAxis,
                signedAngle,
                this.rotateWorldDeltaMatrixBuffer,
            )) {
                this.rotateWorldDeltaMatrixBuffer.identity();
            }
        } else {
            this.rotateWorldDeltaMatrixBuffer.makeRotationFromQuaternion(worldDeltaQuaternion);
        }

        this.linkedTransformObjects.forEach((object) => {
            const baselineTransform = this.linkedTransformBaselineById.get(object.uuid);
            const pivotWorld = this.linkedRotationPivotWorldById.get(object.uuid);
            if (!baselineTransform || !pivotWorld) {
                return;
            }

            /**
             * 世界枢轴矩阵保证旋转中心不再依赖组件原点；再投回父级局部矩阵，
             * 避免父级已旋转或子装配嵌套时把世界轴误当成局部轴。
             */
            if (this.constrainedRotationAxis) {
                /**
                 * Mate 轴投影器已经构建了绕装配轴的 world delta。
                 * 这里不能再叠加包围盒中心，否则会把转动副旋转误变成偏心旋转。
                 */
                this.rotateNextWorldMatrixBuffer
                    .copy(this.rotateWorldDeltaMatrixBuffer)
                    .multiply(baselineTransform.worldMatrix);
            } else {
                this.rotatePivotToWorldMatrixBuffer.makeTranslation(pivotWorld.x, pivotWorld.y, pivotWorld.z);
                this.rotateWorldToPivotMatrixBuffer.makeTranslation(-pivotWorld.x, -pivotWorld.y, -pivotWorld.z);
                this.rotateNextWorldMatrixBuffer
                    .copy(this.rotatePivotToWorldMatrixBuffer)
                    .multiply(this.rotateWorldDeltaMatrixBuffer)
                    .multiply(this.rotateWorldToPivotMatrixBuffer)
                    .multiply(baselineTransform.worldMatrix);
            }

            this.applyEffectiveWorldMatrix(object, this.rotateNextWorldMatrixBuffer);
        });
    }

    /**
     * 解析选中件所在的整个连通装配（可整体自由旋转时）的联动对象。
     *
     * @returns 可整体刚性旋转的连通装配对象；不可整体旋转时为空数组。
     */
    private resolveWholeGroupRotationObjects(): THREE.Object3D[] {
        const componentInstanceId = this.initialComponentSnapshot?.componentInstanceId;
        if (!componentInstanceId) {
            return [];
        }
        const objects = this.renderer.assemblyMateController
            ?.resolveFloatingConnectedLinkedTransformObjectsForTransform?.({ componentInstanceId }) ?? [];
        return [...objects];
    }

    /**
     * 在首个有效转动帧判定并锁定“约束旋转 / 整组刚性旋转”模式。
     *
     * 设计说明：
     * 剩余轴不支持鼠标方向（鼠标旋转轴与剩余转轴夹角超过阈值）时，把整个已提交配合连通装配当作单一自由刚体，
     * 绕鼠标方向整体刚性旋转——整组刚体运动保持全部内部配合、剩余自由度的轴也一起转，因此不会破坏配合。
     * 模式一旦锁定本次拖拽不再切换，避免拖拽轨迹抖动导致约束/整组来回跳变。
     *
     * @param worldDeltaQuaternion 本帧世界空间增量旋转四元数（已归一化）。
     */
    private maybeLockWholeGroupRotationMode(worldDeltaQuaternion: THREE.Quaternion): void {
        if (this.rotationModeDecisionLocked) {
            return;
        }
        const residualAxis = this.constrainedRotationAxis;
        if (!residualAxis || this.wholeGroupRotationObjects.length === 0) {
            this.rotationModeDecisionLocked = true;
            return;
        }

        this.wholeGroupDragAxisBuffer.set(
            worldDeltaQuaternion.x,
            worldDeltaQuaternion.y,
            worldDeltaQuaternion.z,
        );
        const sinHalf = this.wholeGroupDragAxisBuffer.length();
        if (sinHalf <= AssemblyPartDragStrategy.ROTATION_MIN_DRAG_SIN_HALF) {
            // 本帧转动过小，方向不可靠，暂不锁定，等下一帧更明确的方向。
            return;
        }

        this.wholeGroupResidualAxisBuffer.fromArray(residualAxis.direction);
        if (this.wholeGroupResidualAxisBuffer.lengthSq() <= 1e-18) {
            this.rotationModeDecisionLocked = true;
            return;
        }

        this.wholeGroupDragAxisBuffer.multiplyScalar(1 / sinHalf);
        this.wholeGroupResidualAxisBuffer.normalize();
        const aligned = Math.abs(this.wholeGroupDragAxisBuffer.dot(this.wholeGroupResidualAxisBuffer))
            >= AssemblyPartDragStrategy.ROTATION_RESIDUAL_ALIGNMENT_COS;
        this.rotationModeDecisionLocked = true;
        if (!aligned) {
            this.switchToWholeGroupRotation();
        }
    }

    /**
     * 切换到“整个连通装配整体刚性旋转”：把联动对象换成整组、清除剩余轴约束、以整组 bbox 中心为枢轴，
     * 并以当前（本帧旋转尚未应用）姿态为基线重捕获整组联动状态，从而复用既有捕获/恢复/提交/撤销机制。
     */
    private switchToWholeGroupRotation(): void {
        this.linkedTransformObjects.splice(
            0,
            this.linkedTransformObjects.length,
            ...this.wholeGroupRotationObjects,
        );
        this.linkedRotationPivotObjects.splice(
            0,
            this.linkedRotationPivotObjects.length,
            ...this.wholeGroupRotationObjects,
        );
        this.constrainedRotationAxis = null;

        this.captureLinkedComponentSnapshots(this.initialLinkedComponentSnapshotByObjectId);
        this.captureLinkedWorldPositionState(this.linkedWorldPositionBaselineById);
        this.captureLinkedWorldPositionState(this.linkedLastValidWorldPositionById);
        this.captureLinkedQuaternionState(this.linkedQuaternionBaselineById);
        this.captureLinkedQuaternionState(this.linkedLastValidQuaternionById);
        this.captureLinkedTransformState(this.linkedTransformBaselineById);
        this.captureLinkedTransformState(this.linkedLastValidTransformById);
        this.captureLinkedRotationPivotWorldState(this.linkedRotationPivotWorldById);
    }

    /**
     * 从完整局部变换快照恢复联动对象组。
     *
     * @param sourceMap 完整局部变换快照映射。
     */
    private restoreLinkedTransforms(sourceMap: ReadonlyMap<string, AssemblyPartLocalTransformSnapshot>): void {
        this.linkedTransformObjects.forEach((object) => {
            const snapshot = sourceMap.get(object.uuid);
            if (!snapshot) {
                return;
            }

            object.position.copy(snapshot.position);
            object.quaternion.copy(snapshot.quaternion);
            object.scale.copy(snapshot.scale);
            this.updateEffectiveWorldMatrix(object);
        });
    }

    /**
     * 从快照恢复联动对象组的四元数。
     *
     * @param sourceMap 四元数快照映射。
     */
    private restoreLinkedQuaternions(sourceMap: ReadonlyMap<string, THREE.Quaternion>): void {
        this.linkedTransformObjects.forEach((object) => {
            const quaternion = sourceMap.get(object.uuid);
            if (!quaternion) {
                return;
            }

            object.quaternion.copy(quaternion);
            this.updateEffectiveWorldMatrix(object);
        });
    }

    /**
     * 解析 AlongAssemblyAxis 模式下当前拖拽应锁定的装配体轴。
     *
     * 设计说明：
     * 1. 首次发生有效位移时，按局部增量绝对值最大的分量选择 X/Y/Z 之一；
     * 2. 一旦选中，本次拖拽剩余生命周期内保持不变，直到 stop 后才允许重新选轴；
     * 3. 这样可以避免拖动过程中因为鼠标轨迹抖动在不同轴之间来回切换。
     *
     * @param localDelta 装配体局部坐标系下的位移增量。
     * @returns 当前拖拽会话锁定的装配体轴。
     */
    private resolveLockedAssemblyAxis(localDelta: THREE.Vector3): AssemblyAxisLock {
        if (this.lockedAssemblyAxis) {
            return this.lockedAssemblyAxis;
        }

        const absX = Math.abs(localDelta.x);
        const absY = Math.abs(localDelta.y);
        const absZ = Math.abs(localDelta.z);

        if (absX >= absY && absX >= absZ) {
            this.lockedAssemblyAxis = 'x';
            return this.lockedAssemblyAxis;
        }
        if (absY >= absX && absY >= absZ) {
            this.lockedAssemblyAxis = 'y';
            return this.lockedAssemblyAxis;
        }

        this.lockedAssemblyAxis = 'z';
        return this.lockedAssemblyAxis;
    }

    /**
     * 按已锁定的装配体轴裁剪局部位移。
     *
     * @param localDelta 装配体局部坐标系下的位移增量。
     * @param lockedAxis 当前锁定轴。
     */
    private applyLockedAssemblyAxis(localDelta: THREE.Vector3, lockedAxis: AssemblyAxisLock): void {
        if (lockedAxis === 'x') {
            localDelta.y = 0;
            localDelta.z = 0;
            return;
        }
        if (lockedAxis === 'y') {
            localDelta.x = 0;
            localDelta.z = 0;
            return;
        }

        localDelta.x = 0;
        localDelta.y = 0;
    }

    /**
     * 按当前沿实体参考约束裁剪位移。
     *
     * 设计说明：
     * 1. 线性参考只保留沿参考方向的投影，形成 1 自由度；
     * 2. 平面参考去除法向分量，形成平面内 2 自由度；
     * 3. 若参考约束缺失或退化，则回退到零位移，避免错误自由拖动。
     *
     * @param worldDelta 当前世界坐标位移增量。
     * @returns 裁剪后的世界坐标位移增量。
     */
    private applyAlongEntityConstraint(worldDelta: THREE.Vector3): THREE.Vector3 {
        if (!this.alongEntityReference) {
            return this.alongEntityConstrainedDeltaBuffer.set(0, 0, 0);
        }

        if (
            this.alongEntityReference.constraintKind === MoveAlongEntityConstraintKind.LineLike
            && this.alongEntityReference.directionWorld
        ) {
            const direction = this.alongEntityDirectionBuffer
                .copy(this.alongEntityReference.directionWorld)
                .normalize();
            if (direction.lengthSq() <= 1e-12) {
                return this.alongEntityConstrainedDeltaBuffer.set(0, 0, 0);
            }
            return this.alongEntityConstrainedDeltaBuffer
                .copy(direction)
                .multiplyScalar(worldDelta.dot(direction));
        }

        if (
            this.alongEntityReference.constraintKind === MoveAlongEntityConstraintKind.PlaneLike
            && this.alongEntityReference.planeNormalWorld
        ) {
            const planeNormal = this.alongEntityPlaneNormalBuffer
                .copy(this.alongEntityReference.planeNormalWorld)
                .normalize();
            if (planeNormal.lengthSq() <= 1e-12) {
                return this.alongEntityConstrainedDeltaBuffer.set(0, 0, 0);
            }
            const normalProjectionLength = worldDelta.dot(planeNormal);
            return this.alongEntityConstrainedDeltaBuffer
                .copy(worldDelta)
                .sub(planeNormal.multiplyScalar(normalProjectionLength));
        }

        return this.alongEntityConstrainedDeltaBuffer.set(0, 0, 0);
    }

    
    /**
     * 重算依赖当前装配体位姿的基准引用。
     *
     * 设计说明：
     * 1. 默认视口拖动和旋转由本策略直接写入 Object3D 位姿，不经过 MoveService / RotateService；
     * 2. 因此关联基准面、基准轴、基准点的实时重算必须在本策略的最终位姿落点触发；
     * 3. 碰撞回退同样会改变最终有效位姿，也必须立即复算，避免基准引用停留在被拒绝的预览位置。
     */
    private recomputeDependentAssemblyDatumReferences(): void {
        recomputeAssemblyDatumReferences(this.renderer);
    }

    /**
     * 直接更新本次拖动或旋转实际影响的实例矩阵槽，再请求普通场景渲染。
     *
     * 视口拖动可能同时改变线性阵列联动对象和 Mate 求解对象；这里按对象引用去重后一次提交，
     * 避免把实例矩阵同步重新放回所有渲染请求都会经过的公共热路径。
     */
    private requestRenderAfterTransform(): void {
        const changedObjects = new Set<THREE.Object3D>(this.linkedTransformObjects);
        this.latestMateComponentSnapshotByComponentId.forEach((snapshot) => {
            const object = this.renderer.assemblyMateController
                ?.resolveSceneObjectForComponentSnapshot?.(snapshot) as THREE.Object3D | null | undefined;
            if (object) {
                changedObjects.add(object);
            }
        });
        this.renderer.updateImportedModelInstanceMatrices?.([...changedObjects]);
        // 拖拽采样与矩阵写入不丢弃；同一帧内的多次画面请求交给 Viewer 合并。
        this.renderer.requestRender();
    }
    

    /**
     * 停止零件拖拽。
     */
    protected doStop(): void {
        this.commitDragHistoryRecord();
        this.collisionDetector.dispose();
        this.dragPlane = null;
        this.initialTargetPosition = null;
        this.initialTargetWorldPosition = null;
        this.lastValidWorldPosition = null;
        this.dragStartIntersection = null;
        this.initialTargetQuaternion = null;
        this.linkedWorldPositionBaselineById.clear();
        this.linkedLastValidWorldPositionById.clear();
        this.linkedQuaternionBaselineById.clear();
        this.linkedLastValidQuaternionById.clear();
        this.linkedTransformBaselineById.clear();
        this.linkedLastValidTransformById.clear();
        this.linkedRotationPivotWorldById.clear();
        this.initialLinkedComponentSnapshotByObjectId.clear();
        this.initialMateComponentSnapshotByComponentId.clear();
        this.latestMateComponentSnapshotByComponentId.clear();
        this.initialComponentSnapshot = null;
        this.constrainedRotationAxis = null;
        this.wholeGroupRotationObjects = [];
        this.rotationModeDecisionLocked = false;
        this.lockedAssemblyAxis = null;
        this.collisionEnabled = false;
        this.collisionStopAtCollision = false;

        
        // 拖拽结束后通知 UI 层取消高亮。
        useDispatchEvent(AssemblyDragEventName.DragStopped);
        
    }

    /**
     * 将视口直接拖拽收口为一个装配体 transform 历史记录。
     *
     * 设计说明：
     * 1. 视口拖拽直接写 Object3D 位姿，不经过 MoveService / RotateService；
     * 2. 因此必须在 drag strategy 停止时补齐 before/after 记录，保证 mousemove 不入栈、mouseup 入栈；
     * 3. 真正入 ModelHistoryManager 的事务仍由上层 HistoryRuntimeBridge / adapter 负责。
     */
    private commitDragHistoryRecord(): void {
        if (!this.initialComponentSnapshot) {
            return;
        }

        const targetObject = this.targetObject;
        const operation = this.operationMode === AssemblyPartDragOperationMode.Rotate ? 'rotate' : 'move';
        const pendingChangedRecords = this.createLinkedTransformHistoryRecords(operation);
        if (pendingChangedRecords.length === 0) {
            return;
        }

        if (!this.solveCommittedMatesForActiveDrag({ updateDocumentStatus: true })) {
            return;
        }

        const changedRecords = this.createLinkedTransformHistoryRecords(operation);
        const primaryRecord = changedRecords.find((item) => item.before.componentInstanceId === this.initialComponentSnapshot?.componentInstanceId)
            ?? changedRecords[0]
            ?? null;
        const record = primaryRecord && changedRecords.length > 1
            ? {
                ...primaryRecord,
                relatedRecords: changedRecords.filter((item) => item !== primaryRecord),
            }
            : primaryRecord;
        if (!record) {
            return;
        }

        const commitVersion = advanceAssemblyDragHistoryCommitVersion(targetObject);
        const afterSnapshot = record.after;
        const moveComponentController = this.renderer.moveComponentController;
        if (!moveComponentController) {
            this.rollbackLinkedTransformHistoryRecords([record, ...(record.relatedRecords ?? [])]);
            (globalThis as { reportError?: (reportedError: unknown) => void }).reportError?.(
                new Error('装配体拖拽历史提交失败：MoveComponentController 未初始化。'),
            );
            return;
        }

        void moveComponentController
            .commitAssemblyTransformHistoryRecord(record, {
                onCommitFailed: () => {
                    if (!this.shouldRollbackFailedHistoryCommit(targetObject, commitVersion, afterSnapshot)) {
                        return;
                    }

                    this.rollbackLinkedTransformHistoryRecords([record, ...(record.relatedRecords ?? [])]);
                },
            })
            .catch((error) => {
                (globalThis as { reportError?: (reportedError: unknown) => void }).reportError?.(error);
            });
    }

    /**
     * 创建本次联动拖拽的历史记录集合。
     *
     * @param operation 装配体 transform 操作类型。
     * @returns 发生变化的组件历史记录集合。
     */
    private createLinkedTransformHistoryRecords(operation: 'move' | 'rotate'): AssemblyTransformHistoryRecord[] {
        const linkedRecords = this.linkedTransformObjects
            .map((object) => {
                const beforeSnapshot = this.initialLinkedComponentSnapshotByObjectId.get(object.uuid);
                if (!beforeSnapshot) {
                    return null;
                }

                const afterSnapshot = createComponentInstanceSnapshotFromObject({
                    object: object as unknown as WebGpuObject3D,
                    assemblyId: beforeSnapshot.assemblyId,
                    componentInstanceId: beforeSnapshot.componentInstanceId,
                });

                return createAssemblyTransformHistoryRecord(operation, beforeSnapshot, afterSnapshot);
            })
            .filter((record): record is AssemblyTransformHistoryRecord => Boolean(record));
        const mateRecords = [...this.initialMateComponentSnapshotByComponentId.entries()]
            .map(([componentInstanceId, beforeSnapshot]) => {
                const afterSnapshot = this.latestMateComponentSnapshotByComponentId.get(componentInstanceId);
                if (!afterSnapshot) {
                    return null;
                }
                return createAssemblyTransformHistoryRecord(operation, beforeSnapshot, afterSnapshot);
            })
            .filter((record): record is AssemblyTransformHistoryRecord => Boolean(record));

        return [...linkedRecords, ...mateRecords];
    }

    /**
     * 将联动拖拽历史记录统一回滚到 before 状态。
     *
     * @param records 需要回滚的记录集合。
     */
    private rollbackLinkedTransformHistoryRecords(records: readonly AssemblyTransformHistoryRecord[]): void {
        const changedObjects: THREE.Object3D[] = [];
        records.forEach((record) => {
            const targetObject = this.linkedTransformObjects.find((object) => {
                const beforeSnapshot = this.initialLinkedComponentSnapshotByObjectId.get(object.uuid);

                return beforeSnapshot?.componentInstanceId === record.before.componentInstanceId
                    || object.uuid === record.before.componentInstanceId
                    || object.name === record.before.componentInstanceId
                    || object.uuid === record.before.sourceNodeId
                    || object.name === record.before.sourceNodeId;
            }) ?? (this.renderer.assemblyMateController?.resolveSceneObjectForComponentSnapshot(record.before) as THREE.Object3D | null);

            if (targetObject) {
                applyComponentInstanceSnapshotToObject(
                    targetObject as unknown as WebGpuObject3D,
                    record.before,
                );
                changedObjects.push(targetObject);
            }
        });
        recomputeAssemblyDatumReferences(this.renderer);
        this.renderer.updateImportedModelInstanceMatrices?.(changedObjects);
        this.renderer.requestRender();
    }

    /**
     * 判断失败的历史提交是否仍允许回滚当前对象。
     *
     * 设计说明：
     * 只有对象仍处于本次 mouseup 的 after 位姿，并且没有新的拖拽会话推进版本号时，旧异步失败才可以
     * 回滚到 before。这样可以避免旧失败回调覆盖用户后续已经完成的新操作。
     *
     * @param targetObject 本次提交绑定的拖拽对象。
     * @param commitVersion 本次提交版本号。
     * @param afterSnapshot 本次提交时的 after 快照。
     * @returns 是否允许执行失败回滚。
     */
    private shouldRollbackFailedHistoryCommit(
        targetObject: THREE.Object3D,
        commitVersion: number,
        afterSnapshot: ComponentInstanceSnapshot,
    ): boolean {
        if (assemblyDragHistoryCommitVersions.get(targetObject) !== commitVersion) {
            return false;
        }

        const currentSnapshot = createComponentInstanceSnapshotFromObject({
            object: targetObject as unknown as WebGpuObject3D,
            assemblyId: afterSnapshot.assemblyId,
            componentInstanceId: afterSnapshot.componentInstanceId,
        });

        return areComponentInstanceSnapshotsEqual(currentSnapshot, afterSnapshot);
    }
}
